import { useRef, useCallback, useEffect } from 'react'

const BROADCAST_CHANNEL_NAME = 'report-sync'
const WINDOW_BOUNDS_KEY = 'report-window-bounds'

interface WindowBounds {
  x: number
  y: number
  width: number
  height: number
}

interface ScreenInfo {
  left: number
  top: number
  width: number
  height: number
  isPrimary: boolean
  label?: string
}

interface UseReportWindowOptions {
  onReportCreated?: (data: any) => void
  onShellClosed?: () => void
  onShowUI?: (data: any) => void
}

export const useReportWindow = (options: UseReportWindowOptions = {}) => {
  const { onReportCreated, onShellClosed, onShowUI } = options
  const windowRef = useRef<Window | null>(null)
  const channelRef = useRef<BroadcastChannel | null>(null)

  /**
   * Get all available screens (Modern API)
   * this only works for 
   * | Chrome | Edge |
   * this will prompt for permission on first use
   * check if Window Management API is available
   * fallback: return single screen info based on window.screen
   */
  const getScreens = async (): Promise<ScreenInfo[]> => {
    if ('getScreenDetails' in window) {
      try {
        const screenDetails = await (window as any).getScreenDetails()

        console.log(
          '[useReportWindow] Detected screens:',
          screenDetails.screens.length,
        )

        return screenDetails.screens.map((screen: any) => ({
          left: screen.left,
          top: screen.top,
          width: screen.width,
          height: screen.height,
          isPrimary: screen.isPrimary,
          label: screen.label,
        }))
      } catch (e) {
        console.warn(
          '[useReportWindow] Screen API permission denied or error:',
          e,
        )
      }
    }
    return [
      {
        left: 0,
        top: 0,
        width: window.screen.width,
        height: window.screen.height,
        isPrimary: true,
      },
    ]
  }

  // Detect which screen the main app is on
  const getCurrentScreen = (screens: ScreenInfo[]): ScreenInfo | null => {
    const appCenterX = window.screenX + window.outerWidth / 2
    const appCenterY = window.screenY + window.outerHeight / 2

    return (
      screens.find(
        (screen) =>
          appCenterX >= screen.left &&
          appCenterX < screen.left + screen.width &&
          appCenterY >= screen.top &&
          appCenterY < screen.top + screen.height,
      ) || screens[0]
    )
  }

  // Find the "opposite" screen (any screen that's not current)
  const getOppositeScreen = (
    screens: ScreenInfo[],
    currentScreen: ScreenInfo,
  ): ScreenInfo | null => {
    if (screens.length <= 1) return null

    // Find a different screen
    const otherScreen = screens.find(
      (screen) =>
        screen.left !== currentScreen.left || screen.top !== currentScreen.top,
    )

    return otherScreen || null
  }

  // Calculate centered position on a screen
  const getCenteredBounds = (
    screen: ScreenInfo,
    width: number,
    height: number,
  ): WindowBounds => {
    return {
      x: screen.left + Math.round((screen.width - width) / 2),
      y: screen.top + Math.round((screen.height - height) / 2),
      width,
      height,
    }
  }

  // Get saved bounds or calculate new ones
  const getSavedBounds = (): WindowBounds | null => {
    try {
      const saved = localStorage.getItem(WINDOW_BOUNDS_KEY)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (e) {
      console.warn('[useReportWindow] Failed to load window bounds:', e)
    }
    return null
  }

  // Validate if saved bounds are on a currently connected screen
  const isBoundsOnScreen = (
    bounds: WindowBounds,
    screens: ScreenInfo[],
  ): boolean => {
    return screens.some(
      (screen) =>
        bounds.x >= screen.left &&
        bounds.x < screen.left + screen.width &&
        bounds.y >= screen.top &&
        bounds.y < screen.top + screen.height,
    )
  }

  // BroadcastChannel setup
  useEffect(() => {
    channelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME)

    channelRef.current.onmessage = (event) => {
      const { type, ...data } = event.data

      switch (type) {
        case 'SHELL_READY':
          console.log('[useReportWindow] Shell ready:', data.url)
          break
        case 'SHELL_CLOSED':
          console.log('[useReportWindow] Shell closed')
          windowRef.current = null
          onShellClosed?.()
          break
        case 'report-created':
          onReportCreated?.(data)
          break
        case 'show-ui':
          onShowUI?.(data)
          break
      }
    }

    return () => {
      channelRef.current?.close()
    }
  }, [onReportCreated, onShellClosed, onShowUI])

  const openReport = useCallback(async (reportingUrl: string) => {
    // If already open, focus and update URL
    if (windowRef.current && !windowRef.current.closed) {
      windowRef.current.focus()
      const shellUrl = `/report-shell?url=${encodeURIComponent(reportingUrl)}`
      windowRef.current.location.href = shellUrl
      return
    }

    const defaultSize = { width: 1400, height: 900 }
    const screens = await getScreens()
    const savedBounds = getSavedBounds()

    let targetBounds: WindowBounds

    // Priority 1: Use saved bounds if they're on a connected screen
    if (savedBounds && isBoundsOnScreen(savedBounds, screens)) {
      console.log('[useReportWindow] Using saved position:', savedBounds)
      targetBounds = savedBounds
    }
    // Priority 2: Open on opposite monitor
    else if (screens.length > 1) {
      const currentScreen = getCurrentScreen(screens)
      const oppositeScreen = getOppositeScreen(screens, currentScreen!)

      if (oppositeScreen) {
        targetBounds = getCenteredBounds(
          oppositeScreen,
          defaultSize.width,
          defaultSize.height,
        )
        console.log(
          '[useReportWindow] Opening on opposite screen:',
          oppositeScreen.label || 'secondary',
          targetBounds,
        )
      } else {
        targetBounds = getCenteredBounds(
          currentScreen!,
          defaultSize.width,
          defaultSize.height,
        )
      }
    }
    // Priority 3: Center on current screen
    else {
      targetBounds = getCenteredBounds(
        screens[0],
        defaultSize.width,
        defaultSize.height,
      )
      console.log('[useReportWindow] Single monitor, centering:', targetBounds)
    }

    const shellUrl = `/report-shell?url=${encodeURIComponent(reportingUrl)}`
    const features = [
      `width=${targetBounds.width}`,
      `height=${targetBounds.height}`,
      `left=${targetBounds.x}`,
      `top=${targetBounds.y}`,
      'resizable=yes',
      'scrollbars=yes',
    ].join(',')

    console.log('[useReportWindow] Opening with features:', features)

    const popup = window.open(shellUrl, 'report-shell', features)

    if (popup) {
      windowRef.current = popup

      // Force position after open (browsers often ignore left/top)
      setTimeout(() => {
        try {
          if (popup && !popup.closed) {
            popup.moveTo(targetBounds.x, targetBounds.y)
            popup.resizeTo(targetBounds.width, targetBounds.height)
            console.log(
              '[useReportWindow] Moved window to:',
              targetBounds.x,
              targetBounds.y,
            )
          }
        } catch (e) {
          console.warn('[useReportWindow] Could not move window:', e)
        }
      }, 100)
    }
  }, [])

  const closeReport = useCallback(() => {
    windowRef.current?.close()
    windowRef.current = null
  }, [])

  const isOpen = useCallback(() => {
    return windowRef.current !== null && !windowRef.current.closed
  }, [])

  const sendMessage = useCallback((message: any) => {
    channelRef.current?.postMessage(message)
  }, [])

  return { openReport, closeReport, isOpen, sendMessage }
}
